import type { RequirementFrequency } from '@/api/planning/requirements';
import { careerLabel } from '@/utils/career-metadata';
import { PERIOD_NOUNS_ES } from '@/utils/monthly-expectation';

// Dates and Requirements as the integration page shows them (es-CL). Date-only
// values come as ISO dates and are read as such, never shifted by the browser's
// time zone.

const ISO_DATE_LENGTH = 10;
const MINUTES_PER_HOUR = 60;

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';

  const [year, month, day] = value.slice(0, ISO_DATE_LENGTH).split('-');
  return `${day}-${month}-${year}`;
}

// In the company's time zone when given (Shifts), else the browser's.
export function formatDateTime(value: string | null | undefined, timeZone?: string): string {
  if (!value) return '—';

  return new Date(value).toLocaleString('es-CL', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    hourCycle: 'h23', timeZone,
  });
}

export function formatMonth(value: string): string {
  const [year, month] = value.split('-').map(Number);
  const label = new Date(year, month - 1, 1).toLocaleDateString('es-CL', {
    month: 'long', year: 'numeric',
  });

  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function formatRange(startsOn: string | null, endsOn: string | null): string {
  return `${formatDate(startsOn)} al ${endsOn ? formatDate(endsOn) : 'sin término'}`;
}

export function visitsText(visits: number | null | undefined,
  frequency: string | null | undefined): string | null {
  if (!visits) return null;
  const noun = PERIOD_NOUNS_ES[(frequency ?? 'weekly') as keyof typeof PERIOD_NOUNS_ES];

  return `${visits} por ${noun}`;
}

export function durationText(minutes: number | null): string | null {
  if (!minutes) return null;

  return minutes % MINUTES_PER_HOUR === 0 ? `${minutes / MINUTES_PER_HOUR} h` : `${minutes} min`;
}

// A Requirement as a coordinator reads it: "Kinesiólogo/a · 3 por semana · 45 min".
export function requirementText(values: { career: string | null;
  visitsPerPeriod: number | null; frequency: RequirementFrequency | null;
  durationMin: number | null; }): string {
  return [values.career ? careerLabel(values.career) : null,
    visitsText(values.visitsPerPeriod, values.frequency),
    durationText(values.durationMin)].filter(Boolean).join(' · ');
}

// Odoo's own units come in English; a custom one (Sesiones) is shown as written.
const UOMS_ES: Record<string, [string, string]> = {
  Units: ['Unidad', 'Unidades'],
  Hours: ['Hora', 'Horas'],
  Days: ['Día', 'Días'],
};

export function quantityText(quantity: number | null, uom: string | null): string {
  const names = uom ? UOMS_ES[uom] : undefined;
  const unit = names ? names[quantity === 1 ? 0 : 1] : uom;

  return [quantity, unit].filter((part) => part !== null && part !== '').join(' ');
}
